import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, forkJoin, of, throwError } from 'rxjs';
import { map, switchMap, catchError, filter, tap, shareReplay } from 'rxjs/operators';

import { AppConfigService } from 'app/core/config/app-config.service';
import { AuthRepository, UserRole } from '@app/core/auth/auth.repository';
import { StudentService, Student } from './student.service';
import { StudentCourseService, StudentCourse } from './student-course.service';
import { CompaniesService } from './companies.service';
import { SupervisorScopeService } from './supervisor-scope.service';
import { MessageCountersResponse, MessageCountersService } from './message-counters.service';
import { ChecksheetChangeService } from 'app/modules/admin/academy/changes-academy.service';
import { isNotificationDebugEnabled } from 'app/core/debug/notification-debug';

export type { MessageCountersResponse };

/** Roles for which the supervisor view links a student's name to the student page. */
export const STUDENT_PAGE_ROLES: readonly UserRole[] = [
  UserRole.SuperUser, UserRole.SysAdmin, UserRole.CourseAdmin, UserRole.Supervisor,
];

export interface SupervisorStudent {
  student_id: number;
  student_name: string;
  student_email: string;
  student_avatar?: string;
  active_courses: SupervisorStudentCourse[];
  current_course_id?: number; // The course student is currently active on
  unread_messages_count?: number; // Number of unread chat messages (is_notification=0)
  unread_notifications_count?: number; // Number of unread notifications (is_notification=1)
  /** Student switched the AI tutor off — every step needs manual validation */
  ai_opted_out?: boolean;
}

export interface SupervisorStudentsResponse {
  data: SupervisorStudent[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  /** Students matching filters with any unread message, notification, or pending step */
  activity_student_count?: number;
  /** Students matching filters before activity-only narrowing */
  az_student_count?: number;
}

export interface SupervisorStudentCourse {
  student_course_id: number;
  course_id: number;
  course_name: string;
  course_image?: string;
  status_id: number;
  startdate: Date;
  progress?: number;
  is_current: boolean; // The course student is currently working on
  pending_steps_count?: number; // Number of steps with status_id=2 awaiting supervisor review
}

export interface PendingStepsSummary {
  total: number;
  byStudent: Record<number, number>;
  /** student_id -> student_course_id for first course with pending steps */
  firstPendingCourseByStudent: Record<number, number>;
}

@Injectable({
  providedIn: 'root',
})
export class SupervisorService {
  /** Shared cache: every successful getCanonicalCounters() call updates this, so all
   *  subscribers (top-nav badge, student-list badges) always show the same numbers. */
  private _countersCache$ = new BehaviorSubject<MessageCountersResponse | null>(null);

  /** Observable that emits the latest counters whenever any consumer refreshes them. */
  readonly counters$: Observable<MessageCountersResponse> = this._countersCache$.pipe(
    filter((v): v is MessageCountersResponse => v !== null)
  );

  private _pendingStepsCache$ = new BehaviorSubject<PendingStepsSummary | null>(null);

  readonly pendingSteps$: Observable<PendingStepsSummary> = this._pendingStepsCache$.pipe(
    filter((v): v is PendingStepsSummary => v !== null)
  );

  private _studentsCache: { key: string; list$: Observable<SupervisorStudent[]> } | null = null;

  constructor(
    private httpClient: HttpClient,
    private authRepository: AuthRepository,
    private studentService: StudentService,
    private studentCourseService: StudentCourseService,
    private companiesService: CompaniesService,
    private messageCountersService: MessageCountersService,
    private appConfig: AppConfigService,
    private checksheetChangeService: ChecksheetChangeService,
    private supervisorScope: SupervisorScopeService,
  ) {
    this.checksheetChangeService.dataChanges$
      .pipe(filter((data) => this._shouldRefreshBadgesOnStepChange(data)))
      .subscribe(() => this._refreshBadgesAfterStepChange());
  }

  private _debug(label: string, payload?: any, color: string = '#1e40af'): void {
    if (!isNotificationDebugEnabled()) {
      return;
    }
    try {
      // eslint-disable-next-line no-console
      console.log(`%c[SupervisorService] ${label}`, `color:${color};font-weight:600`, payload ?? '');
    } catch {
      // no-op
    }
  }

  /** Step left pending review (status 2) — refresh global pending/message badges. */
  private _shouldRefreshBadgesOnStepChange(data: any): boolean {
    if (!data?.status_update) { return false; }
    const status = Number(
      data.current_step?.status_id ??
      data.current_step?.data?.student_step?.status_id,
    );
    return status === 3 || status === 4;
  }

  private _refreshBadgesAfterStepChange(): void {
    this._debug('refreshBadgesAfterStepChange()', {}, '#0f766e');
    this.invalidateStudents();
    this.refreshPendingStepsSummary().subscribe();
    this.getCanonicalCounters().subscribe();
  }

  /**
   * Get unread counts grouped by student_id for the current supervisor
   * Returns separate counts for chat messages and notifications
   * @returns Observable of Map with student_id as key and {messages, notifications} as value
   */
  getUnreadMessageCounts(): Observable<Map<number, { messages: number; notifications: number }>> {
    return this.getCanonicalCounters().pipe(
      map((counters: MessageCountersResponse) => this._countsByStudent(counters)),
      catchError((error) => {
        console.error('Error fetching unread message counts:', error);
        return of(new Map<number, { messages: number; notifications: number }>());
      })
    );
  }

  /** Drop the cached student list so the next getSupervisorStudents() call refetches it. */
  invalidateStudents(): void {
    this._studentsCache = null;
  }

  /**
   * All visible students (with their enrollments), loaded once per user, company and
   * "my/all" scope and shared by every caller; filtering and paging happen on this list.
   */
  private _allStudents$(): Observable<SupervisorStudent[]> {
    const companyId = this.companiesService.getSelectedCompanyId();
    const key = `${this.authRepository.getUser()?.person_id}|${companyId}|${this.supervisorScope.scope}`;
    if (this._studentsCache?.key === key) {
      return this._studentsCache.list$;
    }

    const list$ = this.studentService
      .getStudents(1, 1000, '', '', '', '', companyId, true, 'supervisor')
      .pipe(
        map((response) => this._toSupervisorStudents(response)),
        catchError((error) => {
          if (this._studentsCache?.key === key) {
            this._studentsCache = null;
          }
          return throwError(() => error);
        }),
        shareReplay(1),
      );
    this._studentsCache = { key, list$ };
    return list$;
  }

  /** Unread counts from the shared counters cache; fetched only when nothing is cached yet. */
  private _unreadCounts$(): Observable<Map<number, { messages: number; notifications: number }>> {
    const cached = this._countersCache$.value;
    return cached ? of(this._countsByStudent(cached)) : this.getUnreadMessageCounts();
  }

  private _countsByStudent(counters: MessageCountersResponse | null): Map<number, { messages: number; notifications: number }> {
    const counts = new Map<number, { messages: number; notifications: number }>();
    Object.entries(counters?.by_student || {}).forEach(([studentId, row]) => {
      const numStudentId = parseInt(studentId, 10);
      if (isNaN(numStudentId)) return;
      counts.set(numStudentId, {
        messages: Number(row?.messages_unread || 0),
        notifications: Number(row?.notifications_needs_action || 0),
      });
    });
    return counts;
  }

  private _toSupervisorStudents(response: any): SupervisorStudent[] {
    const students: any[] = Array.isArray(response?.data) ? response.data : [];
    this._debug('API Response with eager-loaded courses', { students: students.length });

    return students.map((student) => {
      const activeCourses: SupervisorStudentCourse[] = (student.enrolled_courses || []).map((ec: any) => ({
        student_course_id: ec.student_course_id,
        course_id: ec.course_id,
        course_name: ec.course_name || '',
        course_image: ec.course_image,
        status_id: ec.status_id,
        startdate: ec.startdate ? new Date(ec.startdate) : new Date(),
        progress: 0,
        is_current: false, // Will be set by determineCurrentCourse
        pending_steps_count: ec.pending_steps_count || 0,
      }));

      const currentCourse = this.determineCurrentCourse(activeCourses);
      if (currentCourse) {
        currentCourse.is_current = true;
      }

      return {
        student_id: student.t_ot_studentid || 0,
        student_name: student.name || '',
        student_email: student.email || '',
        student_avatar: this._resolveStudentAvatarUrl(student),
        active_courses: activeCourses,
        current_course_id: currentCourse?.student_course_id,
        ai_opted_out: Number(student.ai_opted_out || 0) === 1,
      } as SupervisorStudent;
    });
  }

  /**
   * Load pending step counts (status_id=2) for all visible students in the selected company.
   * Updates shared pendingSteps$ cache used by dashboard, top-nav review icon, and sidebar.
   */
  refreshPendingStepsSummary(pageSize: number = 10000): Observable<PendingStepsSummary> {
    return this.getSupervisorStudents(undefined, undefined, 1, pageSize).pipe(
      map((response) => {
        let total = 0;
        const byStudent: Record<number, number> = {};
        const firstPendingCourseByStudent: Record<number, number> = {};

        (response?.data || []).forEach((student) => {
          const courses = student.active_courses || [];
          const pending = courses.reduce((sum, c) => sum + (c.pending_steps_count || 0), 0);
          if (pending > 0) {
            byStudent[student.student_id] = pending;
            total += pending;
            const firstPending = courses.find((c) => (c.pending_steps_count || 0) > 0);
            if (firstPending) {
              firstPendingCourseByStudent[student.student_id] = firstPending.student_course_id;
            }
          }
        });

        const summary: PendingStepsSummary = { total, byStudent, firstPendingCourseByStudent };
        this._pendingStepsCache$.next(summary);
        this._debug('refreshPendingStepsSummary()', { total, students: Object.keys(byStudent).length }, '#0f766e');
        return summary;
      }),
      catchError((err) => {
        this._debug('refreshPendingStepsSummary() failed', err, '#dc2626');
        const empty: PendingStepsSummary = { total: 0, byStudent: {}, firstPendingCourseByStudent: {} };
        this._pendingStepsCache$.next(empty);
        return of(empty);
      })
    );
  }

  getTotalPendingStepsCount(): number {
    return this._pendingStepsCache$.getValue()?.total ?? 0;
  }

  getPendingStepCount(studentId: number): number {
    return this._pendingStepsCache$.getValue()?.byStudent[studentId] ?? 0;
  }

  getCanonicalCounters(): Observable<MessageCountersResponse> {
    this._debug('getCanonicalCounters() request', {}, '#1d4ed8');
    return this.messageCountersService.getCounters().pipe(
      tap((parsed) => {
        this._debug('getCanonicalCounters() parsed', {
          totals: parsed.totals,
          studentBuckets: Object.keys(parsed.by_student || {}).length,
        }, '#0f766e');
        this._countersCache$.next(parsed);
      }),
    );
  }

  /**
   * Get total unread count (messages + notifications) for all students assigned to the current supervisor
   * @returns Observable of total count (number)
   */
  getTotalUnreadMessageCount(): Observable<number> {
    return this.getUnreadMessageCounts().pipe(
      map((counts: Map<number, { messages: number; notifications: number }>) => {
        let total = 0;
        counts.forEach((value) => {
          total += (value.messages || 0) + (value.notifications || 0);
        });
        return total;
      }),
      catchError((error) => {
        console.error('Error calculating total unread message count:', error);
        return of(0);
      })
    );
  }

  /**
   * Get all students assigned to the current supervisor
   * Uses eager-loaded courses to avoid N+1 queries (single API call instead of 1+N calls)
   * @param filters - Filter options (status, course, company, search term)
   * @param sort - Sort options (student name, course start, course title)
   * @returns Observable of SupervisorStudent array
   */
  getSupervisorStudents(
    filters?: {
      status?: string; // 'top', 'act', 'comp', 'deact', 'nocourse'
      course_title?: string;
      company_name?: string;
      search_term?: string;
      name_starts_with?: string; // Filter by first letter of student name (A-Z)
    },
    sort?: {
      student_name?: 'asc' | 'desc';
      course_start?: 'asc' | 'desc';
      course_title?: 'asc' | 'desc';
    },
    page: number = 1,
    pageSize: number = 20,
    options?: { attentionOnly?: boolean },
  ): Observable<SupervisorStudentsResponse> {
    return this._allStudents$()
      .pipe(
        switchMap((transformedStudents) => {
          if (transformedStudents.length === 0) {
            return of({
              data: [],
              page: page,
              per_page: pageSize,
              total: 0,
              total_pages: 0
            } as SupervisorStudentsResponse);
          }

          return this._unreadCounts$().pipe(
            map((messageCounts) => {
              // Add separate unread message and notification counts to students
              const studentsWithCounts: SupervisorStudent[] = transformedStudents.map(student => {
                const counts = messageCounts.get(student.student_id) || { messages: 0, notifications: 0 };
                return {
                  ...student,
                  unread_messages_count: counts.messages,
                  unread_notifications_count: counts.notifications,
                };
              });

              // Apply filters
              let filtered: SupervisorStudent[] = studentsWithCounts;

              if (filters?.status) {
                filtered = this.applyStatusFilter(filtered, filters.status);
              }

              if (filters?.course_title) {
                filtered = this.applyCourseFilter(filtered, filters.course_title);
              }

              if (filters?.search_term) {
                filtered = this.applySearchFilter(filtered, filters.search_term);
              }

              if (filters?.company_name) {
                filtered = this.applyCompanyFilter(filtered, filters.company_name);
              }

              // Apply name_starts_with filter (A-Z letter filter)
              if (filters?.name_starts_with) {
                const letter = filters.name_starts_with.toUpperCase();
                filtered = filtered.filter(s =>
                  (s.student_name || '').toUpperCase().startsWith(letter)
                );
              }

              const azStudentCount = filtered.length;
              const activityStudentCount = filtered.filter(s => this._studentNeedsAttention(s)).length;

              if (options?.attentionOnly) {
                filtered = filtered.filter(s => this._studentNeedsAttention(s));
                filtered = this._sortByActivity(filtered);
              } else if (sort) {
                filtered = this.applySorting(filtered, sort);
              }

              // Apply pagination
              const total = filtered.length;
              const totalPages = Math.ceil(total / pageSize);
              const startIndex = (page - 1) * pageSize;
              const endIndex = startIndex + pageSize;
              const paginatedData = filtered.slice(startIndex, endIndex);

              return {
                data: paginatedData,
                page: page,
                per_page: pageSize,
                total: total,
                total_pages: totalPages,
                activity_student_count: activityStudentCount,
                az_student_count: azStudentCount,
              } as SupervisorStudentsResponse;
            })
          );
        }),
        catchError((error) => {
          console.error('Error fetching supervisor students:', error);
          return of({
            data: [],
            page: page,
            per_page: pageSize,
            total: 0,
            total_pages: 0
          } as SupervisorStudentsResponse);
        })
      );
  }

  /**
   * Get all active courses for a specific student
   * @param studentId - Student ID
   * @returns Observable of SupervisorStudentCourse array
   */
  getStudentActiveCourses(studentId: number): Observable<SupervisorStudentCourse[]> {
    // Get all enrollments for the student
    // We need to call the API with student_id=0 to get all enrollments
    // Then filter for this specific student
    return this.studentCourseService.getStudentEncrolements(studentId, 0).pipe(
      map((response: any) => {
        const enrollments = response?.data?.enroled_courses || [];

        return enrollments.map((enrollment: any) => {
          return {
            student_course_id: enrollment.student_course_id,
            course_id: enrollment.course_id,
            course_name: enrollment.course_name || '',
            course_image: enrollment.img_small || enrollment.img_big,
            status_id: enrollment.status_id || 1,
            startdate: enrollment.startdate ? new Date(enrollment.startdate) : new Date(),
            progress: this.calculateProgress(enrollment),
            is_current: false, // Will be determined by determineCurrentCourse
          } as SupervisorStudentCourse;
        });
      }),
      catchError((error) => {
        console.error('Error fetching student courses:', error);
        return of([]);
      })
    );
  }

  private _studentNeedsAttention(student: SupervisorStudent): boolean {
    const pending = (student.active_courses || []).reduce(
      (sum, c) => sum + (c.pending_steps_count || 0),
      0,
    );
    return (
      (student.unread_messages_count || 0) > 0 ||
      (student.unread_notifications_count || 0) > 0 ||
      pending > 0
    );
  }

  private _studentAttentionScore(student: SupervisorStudent): number {
    const pending = (student.active_courses || []).reduce(
      (sum, c) => sum + (c.pending_steps_count || 0),
      0,
    );
    return (student.unread_messages_count || 0) + (student.unread_notifications_count || 0) + pending;
  }

  private _sortByActivity(students: SupervisorStudent[]): SupervisorStudent[] {
    return [...students].sort((a, b) => {
      const scoreDiff = this._studentAttentionScore(b) - this._studentAttentionScore(a);
      if (scoreDiff !== 0) { return scoreDiff; }
      return (a.student_name || '').localeCompare(b.student_name || '');
    });
  }

  /**
   * Determine which course is the "current" one for a student
   * Priority: Most recent activity > Highest progress > First active course
   */
  private determineCurrentCourse(courses: SupervisorStudentCourse[]): SupervisorStudentCourse | undefined {
    if (courses.length === 0) return undefined;

    // Filter active courses (status_id 1 or 2 typically means active/in progress)
    const activeCourses = courses.filter(
      (c) => c.status_id === 1 || c.status_id === 2 || c.status_id === 4
    );

    if (activeCourses.length === 0) return courses[0]; // Return first if no active

    // Sort by progress (highest first), then by startdate (most recent first)
    const sorted = [...activeCourses].sort((a, b) => {
      const progressDiff = (b.progress || 0) - (a.progress || 0);
      if (progressDiff !== 0) return progressDiff;

      const dateA = a.startdate?.getTime() || 0;
      const dateB = b.startdate?.getTime() || 0;
      return dateB - dateA; // Most recent first
    });

    const current = sorted[0];
    current.is_current = true;
    return current;
  }

  /**
   * Calculate progress percentage for a course enrollment
   */
  private calculateProgress(enrollment: any): number {
    // TODO: Calculate based on completed steps / total steps
    // For now, return 0 or use status_id as indicator
    if (enrollment.status_id === 4) return 100; // Completed
    if (enrollment.status_id === 2) return 50; // In progress
    return 0;
  }

  /**
   * Apply status filter
   */
  private applyStatusFilter(
    students: SupervisorStudent[],
    status: string
  ): SupervisorStudent[] {
    switch (status) {
      case 'top':
        // Top students - active in last X days (implement based on last activity)
        return students.filter((s) => s.active_courses.length > 0);
      case 'act':
        // All active students
        return students.filter(
          (s) =>
            s.active_courses.some(
              (c) => c.status_id === 1 || c.status_id === 2 || c.status_id === 4
            )
        );
      case 'comp':
        // Completed courses
        return students.filter((s) =>
          s.active_courses.some((c) => c.status_id === 4)
        );
      case 'deact':
        // Deactivated
        return students.filter((s) =>
          s.active_courses.some((c) => c.status_id === 3 || c.status_id === 0)
        );
      case 'nocourse':
        // No course
        return students.filter((s) => s.active_courses.length === 0);
      default:
        return students;
    }
  }

  /**
   * Apply course title filter
   */
  private applyCourseFilter(
    students: SupervisorStudent[],
    courseTitle: string
  ): SupervisorStudent[] {
    if (!courseTitle || courseTitle === '-1') return students;

    return students.filter((s) =>
      s.active_courses.some((c) =>
        c.course_name.toLowerCase().includes(courseTitle.toLowerCase())
      )
    );
  }

  /**
   * Apply search filter (searches in student name and email)
   */
  private applySearchFilter(
    students: SupervisorStudent[],
    searchTerm: string
  ): SupervisorStudent[] {
    if (!searchTerm || searchTerm.trim() === '') return students;

    const searchLower = searchTerm.toLowerCase().trim();
    return students.filter((s) =>
      s.student_name.toLowerCase().includes(searchLower) ||
      s.student_email.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Apply company filter
   */
  private applyCompanyFilter(
    students: SupervisorStudent[],
    companyName: string
  ): SupervisorStudent[] {
    if (!companyName || companyName === '-1') return students;

    // This would require company data in student object
    // For now, return all (company filtering should be done at API level)
    return students;
  }

  /**
   * Apply sorting
   */
  private applySorting(
    students: SupervisorStudent[],
    sort: {
      student_name?: 'asc' | 'desc';
      course_start?: 'asc' | 'desc';
      course_title?: 'asc' | 'desc';
    }
  ): SupervisorStudent[] {
    let sorted = [...students];

    if (sort.student_name) {
      sorted.sort((a, b) => {
        const comparison = a.student_name.localeCompare(b.student_name);
        return sort.student_name === 'asc' ? comparison : -comparison;
      });
    } else if (sort.course_start) {
      sorted.sort((a, b) => {
        const dateA =
          a.current_course_id &&
          a.active_courses.find((c) => c.student_course_id === a.current_course_id)
            ?.startdate?.getTime() || 0;
        const dateB =
          b.current_course_id &&
          b.active_courses.find((c) => c.student_course_id === b.current_course_id)
            ?.startdate?.getTime() || 0;
        const comparison = dateA - dateB;
        return sort.course_start === 'asc' ? comparison : -comparison;
      });
    } else if (sort.course_title) {
      sorted.sort((a, b) => {
        const titleA =
          a.current_course_id &&
          a.active_courses.find((c) => c.student_course_id === a.current_course_id)
            ?.course_name || '';
        const titleB =
          b.current_course_id &&
          b.active_courses.find((c) => c.student_course_id === b.current_course_id)
            ?.course_name || '';
        const comparison = titleA.localeCompare(titleB);
        return sort.course_title === 'asc' ? comparison : -comparison;
      });
    }

    return sorted;
  }

  /**
   * Build a proper authenticated avatar URL for a student.
   * The API may return `profile_image` as either:
   *   (a) a full URL already (e.g. after a backend fix) — use as-is
   *   (b) a raw filename hash (e.g. "98ed6d4d.png") — convert to the API serve endpoint
   * Falls back to `picture` (legacy field) when neither yields a full URL.
   */
  private _resolveStudentAvatarUrl(student: any): string | null {
    const raw = student.profile_image as string | null | undefined;
    if (raw) {
      if (raw.startsWith('http') || raw.startsWith('/')) {
        return raw; // already a proper URL (e.g. after backend fix)
      }
      // Raw filename — build the serve URL using account_id
      const accountId = student.account_id as number | null | undefined;
      if (accountId) {
        return `${this.appConfig.get<string>('api_baseurl', '')}/account/profile-image/${accountId}`;
      }
    }
    return (student.picture as string | null) || null;
  }
}

